import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { PHONE_DISPLAY, PHONE_LOCAL, EMAIL, ADDRESS, waMessage, DEFAULT_WA_MSG } from "@/data";
import Socials from "@/components/Socials";

const cards = [
  {
    id: "phone",
    icon: Phone,
    title: "Phone / WhatsApp",
    lines: [PHONE_DISPLAY, "Same number for calls & WhatsApp"],
    href: `tel:${PHONE_DISPLAY.replace(/\s/g, "")}`,
    action: "Call Now",
  },
  {
    id: "email",
    icon: Mail,
    title: "Email",
    lines: [EMAIL, "Orders & enquiries welcome"],
    href: `mailto:${EMAIL}`,
    action: "Send Email",
  },
  {
    id: "address",
    icon: MapPin,
    title: "Our Location",
    lines: ["91A Sunny Road, Lakefield,", "Benoni, 1501, South Africa"],
    href: "https://www.google.com/maps?q=91A+Sunny+Road,+Lakefield,+Benoni+1501",
    action: "Get Directions",
  },
];

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 sm:py-36 bg-coal/40">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Contact Us"
          title="ORDERS PLACED ON WHATSAPP OR EMAIL"
          sub="Kindly email or contact us on our cell to place orders — we deliver across South Africa."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.1} className="h-full">
              <a
                data-testid={`contact-${c.id}-card`}
                href={c.href}
                target={c.id === "address" ? "_blank" : undefined}
                rel="noreferrer"
                className="group h-full flex flex-col bg-steel/60 border border-gold/10 rounded-2xl p-8 hover:border-gold/40 hover:-translate-y-2 hover:shadow-[0_20px_60px_-15px_rgba(229,169,60,0.25)] transition-all duration-500"
              >
                <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold group-hover:text-obsidian text-gold transition-all duration-500">
                  <c.icon size={22} strokeWidth={2} />
                </div>
                <h3 className="mt-6 font-heading font-extrabold uppercase text-2xl text-white tracking-tight">
                  {c.title}
                </h3>
                {c.lines.map((l) => (
                  <p key={l} className="mt-1.5 text-sm text-zinc-400">
                    {l}
                  </p>
                ))}
                <span className="mt-auto pt-6 text-xs font-bold uppercase tracking-widest text-gold inline-flex items-center gap-2">
                  {c.action} <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                </span>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div data-testid="contact-socials" className="mt-10 flex flex-col items-center gap-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">Follow Us</p>
            <Socials />
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div
            data-testid="contact-map-frame"
            className="mt-10 rounded-2xl overflow-hidden border border-gold/20 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)]"
          >
            <iframe
              data-testid="contact-map"
              title="Auto City Lubricants — 91A Sunny Road, Lakefield, Benoni"
              src="https://www.google.com/maps?q=91A+Sunny+Road,+Lakefield,+Benoni+1501,+South+Africa&output=embed"
              className="w-full h-[24rem] grayscale-[35%] contrast-[1.05]"
              loading="lazy"
            />
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-10 bg-gold rounded-2xl px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <p className="font-heading font-black uppercase text-3xl sm:text-4xl text-obsidian leading-none tracking-tight">
                Ready to order?
              </p>
              <p className="mt-2 text-obsidian/70 text-sm font-medium">
                Call or WhatsApp {PHONE_LOCAL} — unbeatable prices on outstanding quality oils.
              </p>
            </div>
            <a
              data-testid="contact-order-whatsapp-button"
              href={waMessage(DEFAULT_WA_MSG)}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 inline-flex items-center gap-3 bg-obsidian text-gold font-extrabold uppercase tracking-widest text-sm px-8 py-4 rounded-full hover:shadow-[0_0_40px_rgba(0,0,0,0.4)] hover:-translate-y-0.5 transition-all duration-300"
            >
              <MessageCircle size={18} strokeWidth={2.5} /> WhatsApp {PHONE_LOCAL}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
